import React, { useState } from 'react';
import classNames from 'classnames';
import { PrimaryButton } from 'app/common/components/controls/button/button';
import preventDefault from 'app/gamma/src/util/prevent-default';
import { TeamTestIds } from '@trello/test-ids';
import styles from './create-team-panel.less';
import { forTemplate } from 'app/common/lib/util/i18n';
import { TeamTypeSelect } from 'app/src/components/TeamTypeSelect';

const format = forTemplate('create_team');
const idPrefix = `${Date.now()}-`; // for id specificity

interface CreateTeamPanelProps {
  submitCreateTeam: (args: {
    displayName: string;
    teamVerticalType?: string;
    desc?: string;
  }) => void;
  startAnimation: () => void;
  animationStep: number;
}

export const CreateTeamPanel: React.FunctionComponent<CreateTeamPanelProps> = ({
  submitCreateTeam,
  startAnimation,
  animationStep,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamType, setTeamType] = useState<string>();
  const [teamDescription, setTeamDescription] = useState('');

  const isTeamNameValid = teamName.length > 0 && teamName.length <= 100;
  const isTeamTypeValid = !!teamType;

  const teamNameError = teamName.length === 100;

  const onSubmit = () => {
    setSubmitting(true);

    submitCreateTeam({
      displayName: teamName.trim(),
      desc: teamDescription,
      teamVerticalType: teamType,
    });
  };

  const onBlurTeamName = () => {
    if (teamName && animationStep < 1) {
      startAnimation();
    }
  };

  const onChangeTeamName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTeamName(event.target.value);

    if (animationStep === 3) {
      startAnimation();
    }
  };

  const onChangeTeamType = (teamTypeValue: string) => {
    setTeamType(teamTypeValue);
  };

  const onChangeTeamDescription = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setTeamDescription(event.target.value);
  };

  return (
    <form className={styles.buildTeam} onSubmit={preventDefault(onSubmit)}>
      <span className={styles.letsBuildATeam}>
        {format('lets-build-a-team')}
      </span>
      <span className={styles.boostProductivity}>
        {format('boost-productivity')}
      </span>
      <label
        htmlFor={`${idPrefix}create-team-org-display-name`}
        className={classNames({ [styles.formError]: teamNameError })}
      >
        {format('team-name')}
      </label>
      <input
        id={`${idPrefix}create-team-org-display-name`}
        data-test-id={TeamTestIds.CreateTeamNameInput}
        type="text"
        value={teamName}
        onBlur={onBlurTeamName}
        onChange={onChangeTeamName}
        className={classNames(styles.teamNameInput, {
          [styles.formError]: teamNameError,
        })}
        autoFocus
        maxLength={100}
        disabled={submitting}
        placeholder={format('tacos-co')}
      />
      {!teamNameError ? (
        <span className={styles.inputDescriptionText}>
          {format('team-name-description')}
        </span>
      ) : (
        <span
          className={classNames(styles.inputDescriptionText, {
            [styles.formError]: teamNameError,
          })}
        >
          {format('team-name-error')}
        </span>
      )}
      <TeamTypeSelect
        onChange={onChangeTeamType}
        isDisabled={submitting}
        testId={TeamTestIds.CreateTeamTypeInput}
      />
      <label htmlFor={`${idPrefix}create-team-org-description`}>
        {format('team-description')}{' '}
        <span className={styles.optional}>{format('optional')}</span>
      </label>
      <textarea
        id={`${idPrefix}create-team-org-description`}
        rows={6}
        value={teamDescription}
        onChange={onChangeTeamDescription}
        className={styles.teamDescriptionInput}
        disabled={submitting}
        placeholder={format('our-team-organizes-everything-here')}
      />
      <span className={styles.inputDescriptionText}>
        {format('team-description-description')}
      </span>
      <footer className={styles.addTeamFooter}>
        <PrimaryButton
          disabled={!isTeamNameValid || !isTeamTypeValid || submitting}
          type="submit"
          className={styles.continueButton}
          testId={TeamTestIds.CreateTeamSubmitButton}
        >
          {!submitting ? (
            format('continue')
          ) : (
            <div className={styles.spinner} />
          )}
        </PrimaryButton>
      </footer>
    </form>
  );
};
